import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './App.css';
import AdminScreen from './AdminScreen';

const API_URL = ""; 

function App() {
  const [view, setView] = useState('home'); // 'home', 'test', 'result', 'history', 'calibrate', 'admin'
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [newUserName, setNewUserName] = useState('');
  
  const [testMode, setTestMode] = useState('monitoring');
  
  const [testData, setTestData] = useState(null); 
  const [finalResults, setFinalResults] = useState(null);
  const [overallTime, setOverallTime] = useState(null);
  
  const [calibrationData, setCalibrationData] = useState(null);

  useEffect(() => {
    fetchUsers();
    const storedCal = localStorage.getItem('visionguard_cal');
    if (storedCal) {
        setCalibrationData(JSON.parse(storedCal));
    }
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/users`);
      if (res.ok) {
        const data = await res.json();
        setUsers(data);
        if (data.length > 0 && !selectedUserId) {
            setSelectedUserId(data[0].id);
        }
      }
    } catch (err) {
      console.error("Failed to fetch users", err);
    }
  };

  const createUser = async () => {
    if (!newUserName) return;
    try {
      const res = await fetch(`${API_URL}/api/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newUserName })
      });
      if (res.ok) {
        const user = await res.json();
        setUsers([...users, user]);
        setSelectedUserId(user.id);
        setNewUserName('');
      }
    } catch (err) {
      console.error(err);
    }
  };

  const attemptStartTest = () => {
      if (!selectedUserId) return alert("Please select or create a user.");
      if (!calibrationData) {
          setView('calibrate');
      } else {
          startTest(calibrationData);
      }
  };

  const startTest = async (calData) => {
    // Calculate the maximum safe CSS patch size
    // Leave 40px for horizontal margins (20px each side) + 10px gap
    const availableWidth = window.innerWidth - 50; 
    // Leave 300px for headers, subtext, progress bar, and abort button
    const availableHeight = window.innerHeight - 300;
    
    // Max size of the 2x2 grid (must be square)
    const maxGridSize = Math.min(availableWidth, availableHeight, 800);
    // Size of a single patch (minus 5px for half the gap)
    const patchCssSize = Math.floor((maxGridSize / 2) - 5);

    try {
      const res = await fetch(`${API_URL}/api/csf/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          user_id: parseInt(selectedUserId),
          test_mode: testMode,
          css_px_per_cm: calData.cssPxPerCm,
          viewing_distance_cm: calData.viewingDistanceCm,
          device_pixel_ratio: calData.devicePixelRatio,
          patch_css_size: patchCssSize,
          user_agent: navigator.userAgent,
          screen_width: window.screen.width,
          screen_height: window.screen.height
        }),
        credentials: 'include' 
      });

      if (!res.ok) throw new Error("Failed to start session");

      const data = await res.json();
      setTestData(data);
      setOverallTime(null);
      setView('test');
    } catch (err) {
      console.error(err);
      alert("Failed to connect to backend.");
    }
  };

  const onCalibrationComplete = (data) => {
      setCalibrationData(data);
      startTest(data);
  };

  const submitAnswer = async (isCorrect, cpd, reactionTime) => {
    try {
      const res = await fetch(`${API_URL}/api/csf/next`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ correct: isCorrect, cpd: cpd, reaction_time_sec: reactionTime }),
        credentials: 'include' 
      });

      if (res.status === 401) {
         alert("Session expired. Please refresh.");
         return null;
      }

      const data = await res.json();
      return data;
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  const applyNextData = (data) => {
      if (!data) return;
      if (data.test_finished) {
        setFinalResults(data.final_thresholds);
        setOverallTime(data.overall_time_sec);
        setView('result');
      } else {
        setTestData(data);
      }
  };

  const abortTest = () => {
    setView('home');
    setTestData(null);
  };

  return (
    <div className="app-container">
      {view === 'admin' && (
        <AdminScreen onBack={() => setView('home')} />
      )}
      
      {view === 'home' && (
        <HomeScreen 
          users={users}
          selectedUserId={selectedUserId}
          setSelectedUserId={setSelectedUserId}
          newUserName={newUserName}
          setNewUserName={setNewUserName}
          createUser={createUser}
          testMode={testMode}
          setTestMode={setTestMode}
          onStart={attemptStartTest}
          onViewHistory={() => setView('history')}
          onRecalibrate={() => setView('calibrate')}
          onAdmin={() => setView('admin')}
          calibrationData={calibrationData}
        />
      )}
      
      {view === 'calibrate' && (
          <CalibrationScreen onComplete={onCalibrationComplete} initialData={calibrationData} />
      )}

      {view === 'test' && testData && (
        <TestScreen 
          data={testData} 
          onNext={submitAnswer} 
          applyNextData={applyNextData}
          onAbort={abortTest}
        />
      )}
      
      {view === 'result' && (
        <ResultScreen 
          results={finalResults} 
          overallTime={overallTime}
          onBack={() => setView('home')} 
        />
      )}

      {view === 'history' && (
        <HistoryScreen 
          userId={selectedUserId}
          onBack={() => setView('home')}
        />
      )}
    </div>
  );
}

// --- COMPONENT: HOME ---
function HomeScreen({ users, selectedUserId, setSelectedUserId, newUserName, setNewUserName, createUser, testMode, setTestMode, onStart, onViewHistory, onRecalibrate, onAdmin, calibrationData }) {
  return (
    <div className="home-screen">
      <h1>VisionGuard CSF</h1>
      <h2>Contrast Sensitivity Monitoring</h2>
      
      <div className="card">
          <div className="input-group">
            <label>Select User</label>
            <select 
              value={selectedUserId || ''} 
              onChange={(e) => setSelectedUserId(e.target.value)}
            >
              <option value="" disabled>-- Select User --</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>

          <div className="input-group new-user">
            <input 
              type="text" 
              value={newUserName} 
              onChange={(e) => setNewUserName(e.target.value)} 
              placeholder="Or create new user"
            />
            <button className="btn-secondary" onClick={createUser}>Add</button>
          </div>
      </div>

      <div className="card">
          <div className="input-group">
            <label>Test Mode</label>
            <select 
              value={testMode} 
              onChange={(e) => setTestMode(e.target.value)}
            >
              <option value="monitoring">Daily Monitoring (Fast)</option>
              <option value="baseline">Baseline Init (Long)</option>
            </select>
          </div>
      </div>

      <button className="btn-primary" onClick={onStart}>Start Test</button>
      
      <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
          <button className="btn-secondary" onClick={onViewHistory} style={{ flex: 1, padding: '15px' }}>History</button>
          <button className="btn-secondary" onClick={onRecalibrate} style={{ flex: 1, padding: '15px' }}>
              {calibrationData ? 'Recalibrate' : 'Calibrate'}
          </button>
      </div>
      {calibrationData && (
          <p style={{ marginTop: '10px', opacity: 0.6, fontSize: '0.85rem' }}>
              Screen calibrated at {calibrationData.viewingDistanceCm} cm.
          </p>
      )}

      <div style={{ marginTop: '50px' }}>
        <button onClick={onAdmin} style={{ background: 'none', border: '1px solid #444', color: '#888', padding: '5px 15px', fontSize: '12px' }}>
          Admin / Analysis
        </button>
      </div>
    </div>
  );
}

// --- COMPONENT: TEST SCREEN (FREE VIEWING 4-AFC) ---
function TestScreen({ data, onNext, applyNextData, onAbort }) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [feedbackState, setFeedbackState] = useState(null); // 'fade-in' | 'fade-out' | null
  const [isCorrectFeedback, setIsCorrectFeedback] = useState(null);
  const [trialStartTime, setTrialStartTime] = useState(Date.now());

  useEffect(() => {
    setTrialStartTime(Date.now());
  }, [data.trial_count]);

  const handlePlateClick = async (index) => {
    if (isProcessing) return;
    setIsProcessing(true);

    const reactionTime = (Date.now() - trialStartTime) / 1000.0;
    const isCorrect = (index === data.target_index);
    const current_cpd = data.current_cpd;

    // Start feedback fade-in over the actual target
    setIsCorrectFeedback(isCorrect);
    setFeedbackState('fade-in');

    // Wait for the fade-in animation (250ms) AND the backend request concurrently
    const fadeTimer = new Promise(resolve => setTimeout(resolve, 250));
    const nextDataPromise = onNext(isCorrect, current_cpd, reactionTime);

    const [_, nextData] = await Promise.all([fadeTimer, nextDataPromise]);

    // Apply the next data (this changes the images underneath immediately or switches view)
    applyNextData(nextData);

    if (nextData && !nextData.test_finished) {
        // Fade out over the new images (250ms)
        setFeedbackState('fade-out');
        await new Promise(resolve => setTimeout(resolve, 250));
        setFeedbackState(null);
    }
    
    // Clear processing lock
    setIsProcessing(false);
  };

  return (
    <div className="test-screen">
      <div className="test-header">
        <div className="instruction">
          Select the pattern.
        </div>
        <div className="sub-instruction">
          Free viewing allowed. Guess if you don't know.
        </div>
      </div>

      <div className="grid-container-csf" style={{ maxWidth: data.css_size_px ? `${data.css_size_px * 2 + 10}px` : '600px' }}>
        {[0, 1, 2, 3].map(i => {
          const isTarget = (i === data.target_index);
          const showFeedback = isTarget && feedbackState !== null;
          let feedbackClass = "";
          if (showFeedback) {
              feedbackClass = isCorrectFeedback ? "feedback-correct" : "feedback-incorrect";
          }

          return (
            <div 
              key={i} 
              className="csf-plate"
              onClick={() => handlePlateClick(i)}
              style={data.css_size_px ? { width: `${data.css_size_px}px`, height: `${data.css_size_px}px` } : {}}
            >
                <img src={data[`circles_${i}`]} alt="Gabor Patch" style={{ width: '100%', height: '100%', display: 'block' }} />
                {showFeedback && (
                    <div className={`feedback-overlay ${feedbackState} ${feedbackClass}`}></div>
                )}
            </div>
          );
        })}
      </div>

      <div className="progress-bar">
        Progress {Math.min(data.trial_count + 1, data.max_trials)} / {data.max_trials} 
      </div>
      
      <button className="btn-danger" onClick={onAbort} style={{ marginTop: '40px' }}>
        Abort Test
      </button>
    </div>
  );
}

// --- COMPONENT: RESULT ---
function ResultScreen({ results, overallTime, onBack }) {
  return (
    <div className="result-screen">
      <h1>Test Complete</h1>
      {overallTime && (
          <p style={{ opacity: 0.9, color: '#4ade80', fontSize: '1.2rem' }}>
              Test duration: {Math.floor(overallTime / 60)}m {Math.floor(overallTime % 60)}s
          </p>
      )}
      <p style={{ opacity: 0.7 }}>Contrast Sensitivity Thresholds (logCS)</p>
      
      <div className="results-grid">
          {Object.entries(results).map(([cpd, thresh]) => (
              <div key={cpd} className="result-item">
                  <span className="cpd-label">{cpd} cpd</span>
                  <span className="thresh-val">{(-thresh).toFixed(2)} logCS</span>
              </div>
          ))}
      </div>
      
      <button className="btn-primary" onClick={onBack}>
        Back to Start
      </button>
    </div>
  );
}

// --- COMPONENT: HISTORY ---
function HistoryScreen({ userId, onBack }) {
  const [history, setHistory] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState(null);

  useEffect(() => {
    if (userId) {
      fetch(`${API_URL}/api/csf/history?user_id=${userId}`)
        .then(res => res.json())
        .then(data => {
            setHistory(data);
            if(data.length > 0) setSelectedSessionId(data[data.length-1].id);
        })
        .catch(err => console.error(err));
    }
  }, [userId]);

  const selectedSession = history.find(h => h.id === selectedSessionId);

  let chartData = [];
  if (selectedSession) {
      const thresholds = selectedSession.final_thresholds;
      const cpdKeys = Object.keys(thresholds).sort((a,b) => Number(a) - Number(b));
      chartData = cpdKeys.map(key => ({
          cpd: key,
          logCS: Number((-thresholds[key]).toFixed(2))
      }));
  }

  return (
    <div className="history-screen" style={{width: '100%', maxWidth: '600px', marginTop: '40px', textAlign: 'center'}}>
      <h1>Test History</h1>
      
      {!userId ? (
        <p>Please select a user first.</p>
      ) : history.length === 0 ? (
        <p style={{marginTop: '20px', opacity: 0.7}}>No completed tests found.</p>
      ) : (
        <>
          <div className="card" style={{marginTop: '20px'}}>
              <div className="input-group">
                <label>Select Session</label>
                <select 
                    value={selectedSessionId || ''} 
                    onChange={e => setSelectedSessionId(Number(e.target.value))}
                >
                    {history.map(h => {
                        const d = new Date(h.created_at);
                        const dateStr = d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
                        return (
                            <option key={h.id} value={h.id}>
                                {dateStr} - {h.test_type.toUpperCase()}
                            </option>
                        );
                    })}
                </select>
              </div>
          </div>

          {selectedSession && (
              <div className="card" style={{height: '350px', width: '100%', padding: '20px 20px 20px 0'}}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" vertical={false} />
                    <XAxis 
                      dataKey="cpd" 
                      stroke="#ccc" 
                      label={{ value: 'Spatial Frequency (cpd)', position: 'insideBottom', offset: -15, fill: '#ccc' }} 
                    />
                    <YAxis 
                      stroke="#ccc" 
                      domain={[0, 3]} 
                      label={{ value: 'logCS', angle: -90, position: 'insideLeft', fill: '#ccc' }}
                    />
                    <Tooltip contentStyle={{backgroundColor: '#222', border: '1px solid #444', borderRadius: '8px'}} />
                    <Line 
                      type="monotone" 
                      dataKey="logCS" 
                      name="Contrast Sensitivity" 
                      stroke="#3b82f6" 
                      strokeWidth={3} 
                      activeDot={{ r: 8 }} 
                      dot={{r: 4, fill: '#3b82f6'}}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
          )}
        </>
      )}

      <button className="btn-secondary" onClick={onBack} style={{marginTop: '30px', padding: '12px 30px'}}>
        Back to Home
      </button>
    </div>
  );
}

// --- COMPONENT: CALIBRATION ---
function CalibrationScreen({ onComplete, initialData }) {
  const [step, setStep] = useState(1);
  
  const defaultHeight = initialData ? Math.round(initialData.cssPxPerCm * 8.56) : 400;
  const defaultDistance = initialData ? initialData.viewingDistanceCm : 40;

  const [cardHeightPx, setCardHeightPx] = useState(defaultHeight); 
  const [distance, setDistance] = useState(defaultDistance); 

  const handleFinish = () => {
    // 85.6 mm = 8.56 cm
    const cssPxPerCm = cardHeightPx / 8.56;
    const devicePixelRatio = window.devicePixelRatio || 1;
    
    const calData = {
      cssPxPerCm: cssPxPerCm,
      viewingDistanceCm: distance,
      devicePixelRatio: devicePixelRatio
    };
    
    localStorage.setItem('visionguard_cal', JSON.stringify(calData));
    onComplete(calData);
  };

  if (step === 1) {
    const cardWidthPx = cardHeightPx * (53.98 / 85.6);
    return (
      <div className="calibration-screen">
        <h1>Screen Calibration</h1>
        <p style={{ opacity: 0.8, marginBottom: '20px' }}>
            Please hold a standard credit card vertically against the screen. Adjust the slider until the white box matches exactly.
        </p>
        
        <div className="calibration-area" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '350px' }}>
          <div 
            className="credit-card-box"
            style={{ 
                width: `${cardWidthPx}px`, 
                height: `${cardHeightPx}px`,
                backgroundColor: 'white',
                border: '1px solid red',
                borderRadius: `${cardWidthPx * 0.05}px`, // approximate standard rounded corners relative to width
                boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
            }}
          ></div>
        </div>
        
        <div className="calibration-controls" style={{ display: 'flex', alignItems: 'center', gap: '15px', padding: '20px', background: 'var(--panel-bg)', borderRadius: '12px', marginBottom: '20px' }}>
          <button className="btn-circle" style={{ fontSize: '1.5rem', width: '50px', height: '50px', borderRadius: '25px' }} onClick={() => setCardHeightPx(prev => Math.max(100, prev - 10))}>-</button>
          <input 
            type="range" 
            min="100" max="800" 
            value={cardHeightPx} 
            onChange={(e) => setCardHeightPx(Number(e.target.value))}
            style={{ flex: 1 }}
          />
          <button className="btn-circle" style={{ fontSize: '1.5rem', width: '50px', height: '50px', borderRadius: '25px' }} onClick={() => setCardHeightPx(prev => Math.min(800, prev + 10))}>+</button>
        </div>
        
        <button className="btn-primary" onClick={() => setStep(2)}>Next</button>
      </div>
    );
  }

  // Step 2
  return (
      <div className="calibration-screen">
        <h1>Viewing Distance</h1>
        <p style={{ opacity: 0.8, marginBottom: '30px' }}>How far are you from the screen?</p>
        
        <div className="distance-presets" style={{ display: 'flex', gap: '10px', marginBottom: '30px' }}>
          <button className="btn-secondary" style={{ flex: 1, padding: '15px' }} onClick={() => setDistance(60)}>PC (60 cm)</button>
          <button className="btn-secondary" style={{ flex: 1, padding: '15px' }} onClick={() => setDistance(40)}>Smartphone (40 cm)</button>
        </div>
        
        <div className="calibration-controls distance-slider" style={{ background: 'var(--panel-bg)', padding: '30px 20px', borderRadius: '12px', marginBottom: '20px' }}>
            <h2 style={{ textAlign: 'center', fontSize: '2rem', margin: '0 0 20px 0', color: 'var(--accent-color)' }}>{distance} cm</h2>
            <input 
              type="range" 
              min="25" max="100" 
              value={distance} 
              onChange={(e) => setDistance(Number(e.target.value))}
              style={{ width: '100%' }}
            />
        </div>
        
        <button className="btn-primary" onClick={handleFinish}>Finish Calibration</button>
      </div>
  );
}

export default App;